import { reportUiException } from '@/observability/sentry';
import React from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RotateCcw, Home } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router';

interface RouteErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class RouteErrorBoundary extends React.Component<
  { children: React.ReactNode },
  RouteErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    reportUiException(error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <RouteErrorFallback
          error={this.state.error}
          onReset={() => this.setState({ hasError: false })}
        />
      );
    }

    return this.props.children;
  }
}

interface RouteErrorFallbackProps {
  error?: Error;
  onReset: () => void;
}

function RouteErrorFallback({ error, onReset }: RouteErrorFallbackProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleRetry = () => {
    onReset();
    window.location.reload();
  };

  const handleGoHome = () => {
    onReset();
    navigate('/');
  };

  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center max-w-md mx-auto">
        <div className="mb-6">
          <AlertTriangle className="h-16 w-16 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Something went wrong</h1>
          <p className="text-muted-foreground mb-4">
            There was an error loading this page. You can still navigate using the sidebar.
          </p>
          {error && (
            <details className="text-sm text-left bg-muted p-3 rounded-lg mb-4">
              <summary className="cursor-pointer font-medium mb-2">Error details</summary>
              <code className="text-xs break-all">{error.message}</code>
            </details>
          )}
        </div>

        <div className="flex gap-3 justify-center">
          <Button onClick={handleRetry} variant="default">
            <RotateCcw className="h-4 w-4 mr-2" />
            Try again
          </Button>
          <Button onClick={handleGoHome} variant="outline">
            <Home className="h-4 w-4 mr-2" />
            Go to Dashboard
          </Button>
        </div>

        <p className="text-xs text-muted-foreground mt-4">Current route: {location.pathname}</p>
      </div>
    </div>
  );
}
